/**
 * Life tab state (life-hub PH-01): the list read from GET /api/engine/life and what is new since Joseph last opened
 * each capability. "Seen" lives on the device only (localStorage), so nothing is written to the server.
 */

import { signal } from '@preact/signals';
import { api } from '../../core/api/client';
import type { LifeItem, LifeResponse, LifeSection } from '../../core/api/types';

export const SEEN_KEY = 'jarvis.lifeSeen';

/** Items showing "New"; the Life tab's dot. */
export const lifeNewCount = signal(0);

type Seen = Record<string, string>;

function readSeen(): Seen {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Seen : {};
  } catch {
    return {};
  }
}

export function isNew(item: LifeItem, seen: Seen = readSeen()): boolean {
  if (!item.updated_at) return false;
  const at = Date.parse(item.updated_at);
  if (Number.isNaN(at)) return false;
  const last = typeof seen[item.id] === 'string' ? Date.parse(seen[item.id]) : Number.NaN;
  return Number.isNaN(last) || at > last;
}

function countNew(sections: LifeSection[]): number {
  const seen = readSeen();
  return sections.reduce((n, s) => n + s.items.filter((i) => isNew(i, seen)).length, 0);
}

/** The sections, or null when the list can't be read. Updates the tab dot. */
export async function readLife(): Promise<LifeSection[] | null> {
  let res: LifeResponse;
  try {
    res = await api.getLife();
  } catch {
    return null;
  }
  if (!res || res.available !== true || !Array.isArray(res.sections)) return null;
  lifeNewCount.value = countNew(res.sections);
  return res.sections;
}

export function refreshLife(): void {
  void readLife();
}

/** Opening a capability marks its current issue seen and recounts the dot from the list already read. */
export function markSeen(item: LifeItem, sections: LifeSection[]): void {
  if (!item.updated_at) return;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ ...readSeen(), [item.id]: item.updated_at }));
  } catch {
    // Storage full or blocked: the item just stays marked New.
  }
  lifeNewCount.value = countNew(sections);
}
